import { Layout } from '@/components/Layout';
import { useSEO } from '@/lib/useSEO';
import { useCompany } from '@/lib/company';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Building2, Instagram, MessageSquareWarning, ArrowRight } from 'lucide-react';

/**
 * Public /contacto — reads all company details from `useCompany()` so a
 * single source of truth (settings.platform) drives footer, PQR, emails
 * and this page. WhatsApp button uses the canonical `wa.me/57<phone>`.
 */
export default function Contacto() {
  const c = useCompany();
  useSEO({
    title: 'Contáctanos | AutoMarket Colombia',
    description: 'Datos oficiales de contacto — dirección, WhatsApp, correo y redes sociales de AutoMarket Colombia.',
    canonical: '/contacto',
  });

  return (
    <Layout>
      {/* Hero */}
      <section data-testid="contacto-hero" className="bg-slate-900 text-white">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-16">
          <div className="inline-flex items-center gap-2 border border-orange-500/40 bg-orange-500/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.25em] text-orange-300">
            AutoMarket Colombia
          </div>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl font-black tracking-tighter">Contáctanos</h1>
          <p className="mt-4 text-slate-300 max-w-xl">
            Estamos disponibles a través de los siguientes canales oficiales. Para reportes formales
            de pedidos o inconvenientes, radica una PQR — así llega directo al equipo responsable.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="bg-white">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Company card */}
          <div data-testid="contacto-company-card" className="border border-slate-200 bg-slate-50 p-6">
            <div className="inline-flex items-center gap-2 text-orange-600">
              <Building2 className="h-4 w-4" />
              <span className="text-xs font-bold uppercase tracking-[0.25em]">Datos de la empresa</span>
            </div>
            <h2 className="mt-3 font-display text-2xl font-black tracking-tight text-slate-900">
              {c.company_legal_name || 'Automarket Colombia SAS'}
            </h2>
            {c.company_nit && (
              <div className="mt-1 text-sm text-slate-500">
                NIT <span className="font-mono">{c.company_nit}</span>
              </div>
            )}

            {c.company_address && (
              <div className="mt-6 flex items-start gap-3">
                <MapPin className="h-5 w-5 text-slate-500 shrink-0 mt-0.5" />
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Dirección</div>
                  <div className="text-slate-900" data-testid="contacto-address">{c.company_address}</div>
                </div>
              </div>
            )}

            {c.company_phone && (
              <div className="mt-4 flex items-start gap-3">
                <Phone className="h-5 w-5 text-slate-500 shrink-0 mt-0.5" />
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Celular / WhatsApp</div>
                  <a href={c.telHref} className="text-slate-900 hover:underline" data-testid="contacto-phone">
                    {c.phoneDisplay}
                  </a>
                </div>
              </div>
            )}

            {c.company_email && (
              <div className="mt-4 flex items-start gap-3">
                <Mail className="h-5 w-5 text-slate-500 shrink-0 mt-0.5" />
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Correo</div>
                  <a href={c.mailtoHref} className="text-slate-900 hover:underline" data-testid="contacto-email">
                    {c.company_email}
                  </a>
                </div>
              </div>
            )}

            <div className="mt-4 flex items-start gap-3">
              <Mail className="h-5 w-5 text-slate-500 shrink-0 mt-0.5" />
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Correo administrativo</div>
                <a
                  href={`mailto:${c.company_admin_email || 'administracion@automarketcolombia.co'}`}
                  className="text-slate-900 hover:underline"
                  data-testid="contacto-admin-email"
                >
                  {c.company_admin_email || 'administracion@automarketcolombia.co'}
                </a>
              </div>
            </div>

            {c.whatsapp_url && (
              <a
                data-testid="contacto-whatsapp-cta"
                href={c.whatsapp_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white font-bold px-5 py-3 rounded-none transition-colors"
              >
                <MessageIcon /> Escríbenos por WhatsApp
              </a>
            )}
          </div>

          {/* Social + support */}
          <div className="space-y-6">
            <div data-testid="contacto-socials-card" className="border border-slate-200 bg-white p-6">
              <div className="inline-flex items-center gap-2 text-orange-600">
                <Instagram className="h-4 w-4" />
                <span className="text-xs font-bold uppercase tracking-[0.25em]">Redes sociales</span>
              </div>
              <h3 className="mt-2 font-display text-xl font-black tracking-tight text-slate-900">Síguenos</h3>
              <ul className="mt-4 space-y-3">
                {c.socials.length === 0 && (
                  <li className="text-sm text-slate-500">Próximamente en redes sociales.</li>
                )}
                {c.socials.map((s) => (
                  <li key={s.slug}>
                    <a
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`contacto-social-${s.slug}`}
                      className="inline-flex items-center gap-3 group"
                    >
                      <span className={`h-10 w-10 inline-flex items-center justify-center bg-slate-900 rounded-md transition-colors ${s.hover}`}>
                        <img
                          src={`https://cdn.simpleicons.org/${s.slug}/ffffff`}
                          alt={s.label}
                          className="h-4 w-4"
                          loading="lazy"
                        />
                      </span>
                      <div>
                        <div className="font-semibold text-slate-900 group-hover:text-blue-700">{s.label}</div>
                        <div className="text-xs text-slate-500 truncate max-w-[280px]">{s.href.replace(/^https?:\/\//, '')}</div>
                      </div>
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div data-testid="contacto-support-card" className="border border-slate-200 bg-slate-900 text-white p-6">
              <div className="inline-flex items-center gap-2 text-orange-400">
                <MessageSquareWarning className="h-4 w-4" />
                <span className="text-xs font-bold uppercase tracking-[0.25em]">Soporte y PQR</span>
              </div>
              <h3 className="mt-2 font-display text-xl font-black tracking-tight">¿Un pedido con problemas?</h3>
              <p className="mt-2 text-sm text-slate-300">
                Nuestro canal oficial de reclamos es el sistema PQR — cada caso queda registrado y
                se atiende bajo un tiempo de respuesta comprometido.
              </p>
              <Link
                to="/pqr"
                data-testid="contacto-pqr-cta"
                className="mt-5 inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-slate-900 font-bold px-5 py-3 rounded-none transition-colors"
              >
                Radicar PQR <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}

// Inline WhatsApp icon — avoids extra dep.
function MessageIcon() {
  return (
    <img src="https://cdn.simpleicons.org/whatsapp/ffffff" alt="WhatsApp" className="h-4 w-4" loading="lazy" />
  );
}
